/* Page Inscription (/inscription) — Factumis app.
   Création de compte self-serve : nom, email, mot de passe (min 8).
   Protégée par Cloudflare Turnstile (plugin captcha côté backend) : le token
   est transmis via l'en-tête `x-captcha-response` attendu par le plugin.
   Sur succès → /verifier-email (la vérif email est obligatoire avant accès). */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { safeNext } from '../lib/nav';
import { AuthLayout } from './AuthLayout';
import { Button, Field, Input } from '../components/ui';
import { Icon } from '../lib/icons';

/* Clé de site Turnstile. En prod, fournir la vraie clé via VITE_TURNSTILE_SITE_KEY
   (sinon le défi échouera face au vrai secret serveur). En dev, fallback sur la clé
   de test Cloudflare `1x00000000000000000000AA`, qui réussit toujours le défi côté client. */
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      reset: (id?: string) => void;
      remove: (id: string) => void;
    };
  }
}

/* Mappe les erreurs Better Auth vers un message FR utilisateur. */
function frenchMessage(error: { code?: string; status?: number }): string {
  if (error.code === 'USER_ALREADY_EXISTS') return 'Un compte existe déjà avec cet email.';
  if (error.code === 'PASSWORD_TOO_SHORT') return 'Le mot de passe doit contenir au moins 8 caractères.';
  // Codes émis par le plugin captcha quand le défi est absent ou invalide.
  if (error.code === 'MISSING_RESPONSE' || error.code === 'VERIFICATION_FAILED')
    return 'Vérification anti-robot échouée, réessayez.';
  if (error.status === 429) return 'Trop de tentatives, réessayez dans un instant.';
  return 'Une erreur est survenue, réessayez.';
}

export default function Inscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNext(searchParams.get('next'));
  const [name, setName] = useState('');
  const rawEmailParam = searchParams.get('email') ?? '';
  const [email, setEmail] = useState(/.+@.+/.test(rawEmailParam) ? rawEmailParam : '');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const renderedRef = useRef(false);

  // Charge le script Turnstile une seule fois puis rend le widget en mode
  // explicite pour récupérer le token dans l'état React via le callback.
  useEffect(() => {
    function renderWidget() {
      if (renderedRef.current || !widgetRef.current || !window.turnstile) return;
      renderedRef.current = true;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t: string) => setToken(t),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
      });
    }

    // Démonte proprement le widget pour éviter les widgets fantômes
    // ("widget already rendered") lors d'un remontage de la route.
    function cleanup() {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      renderedRef.current = false;
    }

    if (window.turnstile) {
      renderWidget();
      return cleanup;
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', renderWidget);
      return () => {
        existing.removeEventListener('load', renderWidget);
        cleanup();
      };
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', renderWidget);
    document.head.appendChild(script);
    return () => {
      script.removeEventListener('load', renderWidget);
      cleanup();
    };
  }, []);

  const busy = loading;

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (!token) {
      setErr('Confirmez que vous n’êtes pas un robot.');
      return;
    }
    setLoading(true);
    const { error } = await authClient.signUp.email(
      { name, email, password, callbackURL: next },
      { headers: { 'x-captcha-response': token } },
    );
    setLoading(false);
    if (error) {
      setErr(frenchMessage(error));
      // Le token Turnstile est à usage unique : on le réinitialise après échec.
      setToken(null);
      window.turnstile?.reset(widgetIdRef.current ?? undefined);
      return;
    }
    navigate('/verifier-email', { state: { email } });
  }

  return (
    <AuthLayout title="Créer un compte" subtitle="Quelques secondes pour démarrer votre facturation conforme.">
      {err ? (
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          {err}
        </div>
      ) : null}

      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Field label="Nom">
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            placeholder="Votre nom"
            disabled={busy}
            required
          />
        </Field>
        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="vous@entreprise.fr"
            disabled={busy}
            required
          />
        </Field>
        <Field label="Mot de passe" help="8 caractères minimum.">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            disabled={busy}
            required
          />
        </Field>

        <div ref={widgetRef} className="cf-turnstile" />

        <div className="auth-actions">
          <Button type="submit" full loading={loading} disabled={busy || !token}>
            Créer mon compte
          </Button>
        </div>
      </form>

      <div className="auth-foot-row">
        Déjà un compte ?{' '}
        <Link
          to={next !== '/dashboard' ? `/connexion?next=${encodeURIComponent(next)}` : '/connexion'}
          className="auth-link"
        >
          Se connecter
        </Link>
      </div>
    </AuthLayout>
  );
}
